import React, {useEffect, useMemo, useRef, useState} from 'react';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import {formatNumberWithComma} from '../../../utils/utils';
import {getLoadFiles} from '../../../api/EcaManagementService'; // Assuming this API is created
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {t} from 'i18next';
import {ReactComponent as External} from '../../../assests/svg/external.svg';
import {ReactComponent as Indexed} from '../../../assests/svg/indexed-icon.svg';
import {LOAD_TYPE} from '../../../constants/constants';
import {
  EcaSummaryData,
  LoadFileData,
} from '../../client-details/project-data-table/types';
import {
  setAllDocumentsData,
  setLoadFiles,
  setSelectedLoadFile,
} from '../../../redux/slices/ecaAdhocSearchSlice'; // Importing the action
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface LoadsTabProps {
  activeLoadFile: LoadFileData | null;
  onSelectLoadFile: (
    loadFile: LoadFileData | EcaSummaryData,
    event?: React.MouseEvent<HTMLElement>
  ) => void;
  ecaSummaryData: LoadFileData | null;
  loadWithFlyout?: boolean;
  renderOnPage?: 'eca-home' | 'doc-search';
}

const LoadsTab: React.FC<LoadsTabProps> = ({
  activeLoadFile,
  onSelectLoadFile,
  ecaSummaryData,
  renderOnPage,
}) => {
  const [loadFiles, setLoadFilesState] = useState<LoadFileData[]>([]); // Local state for load files
  const [loading, setLoading] = useState(false);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const {projectId} = useSelector((state: RootState) => state.reports);
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {
    globalSelectedItem,
    globalSelectedScopeItem,
    showSetScopeLoader,
    selectedLoadFile,
  } = useSelector((state: RootState) => state.ecaAdhocSearch);

  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const allDocRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (ecaSummaryData) {
      dispatch(
        setSelectedLoadFile({...ecaSummaryData, type: LOAD_TYPE.ALL_DOCUMENT})
      );
      onSelectLoadFile({...ecaSummaryData, type: LOAD_TYPE.ALL_DOCUMENT});
      dispatch(
        setAllDocumentsData({...ecaSummaryData, type: LOAD_TYPE.ALL_DOCUMENT})
      );
    }
  }, [ecaSummaryData, dispatch]);

  const fetchLoadFiles = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const paramString = `clientId=${clientId}&projectId=${projectId}`;
      const response = await getLoadFiles(paramString);
      const res = response.data;
      const data = res?.data || [];
      setLoadFilesState(data);
      dispatch(setLoadFiles(data));
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoadFiles();
  }, [projectId]);

  useEffect(() => {
    if (activeLoadFile) {
      dispatch(setSelectedLoadFile(activeLoadFile)); // Update Redux with the active load file
    }
  }, [activeLoadFile, dispatch]);

  useEffect(() => {
    if (searchKeyword !== '') {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SEARCH,
        click: {
          name: t('gaTracking.ecaHomePage.filterList'),
          region: GA_REGIONS.LEFT_NAV_TAB,
          type: GA_CLICK_TYPES.LIST,
          action: t('gaTracking.ecaHomePage.searchedToFilter'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.loads'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
  }, [searchKeyword]);

  const filteredLoadFiles = loadFiles.filter(
    (item) =>
      item.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      item.evidenceSource.toLowerCase().includes(searchKeyword.toLowerCase())
  );
  const handleSelectLoadFile = (
    loadFile: LoadFileData,
    event: React.MouseEvent<HTMLElement>
  ) => {
    dispatch(setSelectedLoadFile(loadFile)); // Update the selected load file in Redux
    if (renderOnPage === 'eca-home') {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILTER,
        click: {
          name: t('gaTracking.ecaHomePage.folder'),
          region: GA_REGIONS.WIDGET_LEFT_NAV,
          type: GA_CLICK_TYPES.LIST_ITEM,
          action: t('gaTracking.ecaHomePage.filteredDocuments'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.loads'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
    onSelectLoadFile(loadFile, event);
  };

  const progressStatus = ['queued', 'new', 'retrying', 'inprogress'];
  const completeStatus = ['completedwitherror', 'completed', 'indexclosed'];

  const renderFolderIcon = useMemo(() => {
    return (item: LoadFileData) => {
      if (item.status) {
        const status = item.status.toLowerCase();
        if (progressStatus.includes(status)) {
          return <Indexed className="other-icon" aria-hidden="true" />;
        } else if (completeStatus.includes(status)) {
          return <FolderIcon className="folder-icon" />;
        } else {
          return <External className="other-icon" aria-hidden="true" />;
        }
      }
    };
  }, []);

  const failedToLoadContent = () => (
    <AurDataGridErrorOverlay
      onRefresh={() => {
        if (isFailedToLoadContent) fetchLoadFiles();
      }}
    />
  );

  return (
    <>
      <Box className="loadsTab">
        <div className="load-tab-section">
          <AurSearchBox
            classNameProps="filter-text-field"
            label={t('common.filterList')}
            onSearch={setSearchKeyword}
          />
        </div>
        {loading ? (
          <Box className="eca-tab-loader">
            <AurLoadingIndicator show={true} />
          </Box>
        ) : isFailedToLoadContent ? (
          failedToLoadContent()
        ) : (
          <List className="listWithIcons drawer-treeview-content">
            {ecaSummaryData !== null && (
              <ListItem ref={allDocRef} disablePadding>
              <ListItemButton
                component="button"
                onClick={(event) => {
                  handleSelectLoadFile(
                    {...ecaSummaryData, type: LOAD_TYPE.ALL_DOCUMENT},
                    event
                  );
                }}
                onKeyUp={(event) => {
                  if (event.key == 'Enter' || event.key == ' ') {
                    handleSelectLoadFile(
                      {...ecaSummaryData, type: LOAD_TYPE.ALL_DOCUMENT},
                      event as any
                    );
                  }
                }}
                className={`${
                  selectedLoadFile?.type === LOAD_TYPE.ALL_DOCUMENT
                    ? 'selected'
                    : ''
                } ${globalSelectedItem?.type === LOAD_TYPE.ALL_DOCUMENT ? 'globalSelected' : ''}`}
              >
                <ListItemIcon>
                  <FolderIcon className="folder-icon" />
                </ListItemIcon>
                <ListItemText
                  className="typo-text body2_size"
                  primary={
                    <>
                      {t('ecaHomepageModals.ecaBottom.allDocuments')}{' '}
                      <span className="count">
                        (
                        {formatNumberWithComma(
                          ecaSummaryData.totalDocumentsCount
                        )}
                        )
                      </span>
                    </>
                  }
                />
              </ListItemButton>
              </ListItem>
            )}
            {filteredLoadFiles.map((item, index) => (
              <ListItem key={index} disablePadding>
              <ListItemButton
                component="button"
                onClick={(event) => {
                  handleSelectLoadFile(
                    {...item, type: LOAD_TYPE.LOAD_DOCUMENT},
                    event
                  );
                }}
                onKeyUp={(event) => {
                  if (event.key == 'Enter' || event.key == ' ') {
                    handleSelectLoadFile(
                      {...item, type: LOAD_TYPE.LOAD_DOCUMENT},
                      event as any
                    );
                  }
                }}
                className={`load-fileList ${
                  selectedLoadFile?.id === item.id ? 'selected' : ''
                } ${globalSelectedItem?.id === item.id ? 'globalSelected' : ''} ${
                  globalSelectedItem?.id !== item.id &&
                  globalSelectedScopeItem?.id === item.id
                    ? 'globalSelectedScope'
                    : ''
                }`}
              >
                <ListItemIcon>{renderFolderIcon(item)}</ListItemIcon>
                {globalSelectedScopeItem?.id === item.id && (
                  <ListItemIcon>
                    <AurIcon
                      icon={IconNames.ScopeIcon}
                      color={IconColors.Custom}
                      customClassName={'other-icon scope-icon'}
                    />
                  </ListItemIcon>
                )}

                <ListItemText
                  primary={
                    <>
                      <span className="typo-text body2_size name">
                        {item.name}
                      </span>
                      <span className="typo-text body2_size count">
                        {' '}
                        ({formatNumberWithComma(item.totalDocumentsCount)})
                        {item.evidenceSource === 'External' && (
                          <span className="typo-text body4_size custom_text">
                            {t('ecaHomepageModals.ecaBottom.external')}
                          </span>
                        )}
                      </span>
                    </>
                  }
                  secondary={
                    item?.status &&
                    progressStatus.includes(item?.status.toLowerCase()) && (
                      <span className="s-text">
                        {formatNumberWithComma(
                          item.totalNotIndexedDocumentCount
                        )}{' '}
                        {t('ecaHomepageModals.ecaBottom.documentIndex')}
                      </span>
                    )
                  }
                />
              </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      {showSetScopeLoader && (
        <Box className="left-scope-loader">
          <AurLoadingIndicator
            show={showSetScopeLoader}
            className="loader-icon"
          />
        </Box>
      )}
    </>
  );
};

export default LoadsTab;
